import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Text,
  Title,
  Group,
  Badge,
  Stack,
  Button,
  Progress,
  RingProgress,
  Card,
  ThemeIcon,
  SimpleGrid,
  ActionIcon,
  Tooltip,
  Alert
} from '@mantine/core';
import {
  IconHome,
  IconUsers,
  IconCalendar,
  IconBrush,
  IconCreditCard,
  IconTrendingUp,
  IconTrendingDown,
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconRefresh,
  IconBuilding,
  IconReceipt,
  IconChartBar
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

function ModernDashboard() {
  const [stats, setStats] = useState({
    totalUnits: 0,
    occupiedUnits: 0,
    cleanUnits: 0,
    dirtyUnits: 0,
    todayCheckIns: 0,
    todayCheckOuts: 0,
    monthlyRevenue: 0,
    occupancyRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      // Simulate API calls - replace with actual API endpoints
      setTimeout(() => {
        setStats({
          totalUnits: 150,
          occupiedUnits: 120,
          cleanUnits: 95,
          dirtyUnits: 25,
          todayCheckIns: 15,
          todayCheckOuts: 12,
          monthlyRevenue: 125000,
          occupancyRate: 80
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to load dashboard statistics',
        color: 'red',
      });
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, trend, trendValue }) => (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Group justify="space-between" mb="xs">
        <Text size="sm" c="dimmed" fw={500}>
          {title}
        </Text>
        <ThemeIcon color={color} variant="light" size="lg">
          <Icon size="1.5rem" />
        </ThemeIcon>
      </Group>
      
      <Group align="baseline" gap="xs">
        <Text size="xl" fw={700}>
          {value}
        </Text>
        {trend && (
          <Group gap="xs">
            {trend === 'up' ? (
              <IconTrendingUp size="1rem" color="var(--mantine-color-green-6)" />
            ) : (
              <IconTrendingDown size="1rem" color="var(--mantine-color-red-6)" />
            )}
            <Text size="sm" c={trend === 'up' ? 'green' : 'red'}>
              {trendValue}%
            </Text>
          </Group>
        )}
      </Group>
    </Paper>
  );

  const QuickActionCard = ({ title, description, icon: Icon, color, onClick }) => (
    <Card shadow="sm" padding="lg" radius="md" withBorder style={{ cursor: 'pointer' }} onClick={onClick}>
      <Group mb="md">
        <ThemeIcon color={color} variant="light" size="lg">
          <Icon size="1.5rem" />
        </ThemeIcon>
        <Text fw={600} size="lg">
          {title}
        </Text>
      </Group>
      <Text size="sm" c="dimmed">
        {description}
      </Text>
    </Card>
  );

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="center">
          <div>
            <Title order={1} c="dark">
              Dashboard
            </Title>
            <Text c="dimmed" size="lg">
              Welcome back! Here's what's happening at your resort.
            </Text>
          </div>
          <Group gap="sm">
            <Tooltip label="Refresh Data">
              <ActionIcon variant="outline" onClick={fetchDashboardStats}>
                <IconRefresh size="1.2rem" />
              </ActionIcon>
            </Tooltip>
            <Badge color="green" variant="light" size="lg">
              System Online
            </Badge>
          </Group>
        </Group>

        {/* Key Metrics */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          <StatCard
            title="Total Units"
            value={stats.totalUnits}
            icon={IconBuilding}
            color="blue"
            trend="up"
            trendValue="5"
          />
          <StatCard
            title="Occupied Units"
            value={stats.occupiedUnits}
            icon={IconUsers}
            color="green"
            trend="up"
            trendValue="12"
          />
          <StatCard
            title="Clean Units"
            value={stats.cleanUnits}
            icon={IconCheck}
            color="teal"
          />
          <StatCard
            title="Dirty Units"
            value={stats.dirtyUnits}
            icon={IconBrush}
            color="orange"
          />
        </SimpleGrid>

        {/* Today's Activity */}
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          <StatCard
            title="Today's Check-ins"
            value={stats.todayCheckIns}
            icon={IconCalendar}
            color="cyan"
            trend="up"
            trendValue="8"
          />
          <StatCard
            title="Today's Check-outs"
            value={stats.todayCheckOuts}
            icon={IconClock}
            color="violet"
          />
          <StatCard
            title="Monthly Revenue"
            value={`$${stats.monthlyRevenue.toLocaleString()}`}
            icon={IconReceipt}
            color="green"
            trend="up"
            trendValue="15"
          />
        </SimpleGrid>

        {/* Occupancy Rate */}
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow="sm" p="xl" radius="md" withBorder>
              <Title order={3} mb="md">
                Occupancy Rate
              </Title>
              <Group justify="center" mb="md">
                <RingProgress
                  size={120}
                  thickness={12}
                  sections={[
                    { value: stats.occupancyRate, color: 'blue' }
                  ]}
                  label={
                    <Text ta="center" size="lg" fw={700}>
                      {stats.occupancyRate}%
                    </Text>
                  }
                />
              </Group>
              <Text ta="center" c="dimmed">
                Current occupancy rate across all units
              </Text>
            </Paper>
          </Grid.Col>
          
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Paper shadow="sm" p="xl" radius="md" withBorder>
              <Title order={3} mb="md">
                Housekeeping Status
              </Title>
              <Stack gap="md">
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Clean Units</Text>
                    <Text size="sm" fw={500}>
                      {stats.cleanUnits}/{stats.totalUnits}
                    </Text>
                  </Group>
                  <Progress value={(stats.cleanUnits / stats.totalUnits) * 100} color="green" />
                </div>
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Dirty Units</Text>
                    <Text size="sm" fw={500}>
                      {stats.dirtyUnits}/{stats.totalUnits}
                    </Text>
                  </Group>
                  <Progress value={(stats.dirtyUnits / stats.totalUnits) * 100} color="orange" />
                </div>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>

        {/* Quick Actions */}
        <div>
          <Title order={3} mb="md">
            Quick Actions
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <QuickActionCard
              title="New Reservation"
              description="Create a new guest reservation"
              icon={IconCalendar}
              color="blue"
              onClick={() => {/* Navigate to new reservation */}}
            />
            <QuickActionCard
              title="Check In"
              description="Process guest check-in"
              icon={IconUsers}
              color="green"
              onClick={() => {/* Navigate to check-in */}}
            />
            <QuickActionCard
              title="Housekeeping"
              description="Manage room cleaning status"
              icon={IconBrush}
              color="orange"
              onClick={() => {/* Navigate to housekeeping */}}
            />
            <QuickActionCard
              title="Reports"
              description="View system reports"
              icon={IconChartBar}
              color="violet"
              onClick={() => {/* Navigate to reports */}}
            />
          </SimpleGrid>
        </div>

        {/* Alerts */}
        <Alert icon={<IconAlertCircle size="1rem" />} title="System Notice" color="blue">
          All systems are operating normally. Last backup completed successfully at 2:00 AM.
        </Alert>
      </Stack>
    </Container>
  );
}

export default ModernDashboard;
